/**
 * Listings Page Component
 * Browse all or filtered listings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useListing } from '@/hooks/useListing';
import { Listing } from '@/types/listing';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { Filter, X } from 'lucide-react';
import { formatCategory } from '@/lib/utils';
import SEO from '@/components/common/SEO';
import {
  AdsterraBanner320x50,
  AdsterraNativeBanner,
  AdsterraSmartlinkPromo,
} from '@/components/ads';

// Category-specific placeholder images using picsum with consistent seeds
const categoryPlaceholders: Record<string, string> = {
  CARS: 'https://picsum.photos/seed/cars-listing/400/400',
  BIKES: 'https://picsum.photos/seed/bikes-listing/400/400',
  MOBILES: 'https://picsum.photos/seed/mobile-listing/400/400',
  REAL_ESTATE: 'https://picsum.photos/seed/real-estate-listing/400/400',
  RENT_ROOMMATES: 'https://picsum.photos/seed/rent-roommates-listing/400/400',
  BUS_TRUCKS_AUTOS_VEHICLES: 'https://picsum.photos/seed/bus-truck-auto-listing/400/400',
  AUTOMOBILE_ACCESSORIES: 'https://picsum.photos/seed/auto-accessories/400/400',
  PETS_AND_HOBBIES: 'https://picsum.photos/seed/pets-hobbies/400/400',
  COMMUNITY_AND_EVENTS: 'https://picsum.photos/seed/community-events/400/400',
  FURNITURE_INTERIORS: 'https://picsum.photos/seed/furniture-interiors/400/400',
  ELECTRONICS_AND_APPLIANCES: 'https://picsum.photos/seed/electronics-appliances/400/400',
  PERSONAL_AND_COMMUNITY: 'https://picsum.photos/seed/personal-community/400/400',
  JOBS_AND_SERVICES: 'https://picsum.photos/seed/jobs-services/400/400',
  FASHION_AND_LIFESTYLE: 'https://picsum.photos/seed/fashion-lifestyle/400/400',
  SAFETY_PROTECTION: 'https://picsum.photos/seed/safety-protection/400/400',
  BUSINESS_FINANCIAL: 'https://picsum.photos/seed/business-financial/400/400',
  PROFESSIONAL_SERVICES: 'https://picsum.photos/seed/professional-services/400/400',
  INSURANCE_LEGAL: 'https://picsum.photos/seed/insurance-legal/400/400',
  MAINTENANCE_AND_REPAIR: 'https://picsum.photos/seed/maintenance-repair/400/400',
  PERSONAL_SERVICES: 'https://picsum.photos/seed/personal-services/400/400',
  SALES_AUCTIONS: 'https://picsum.photos/seed/sales-auctions/400/400',
  GIFTS_SPECIAL_OCCASIONS: 'https://picsum.photos/seed/gifts-occasions/400/400',
  TRAVEL_ENTERTAINMENT: 'https://picsum.photos/seed/travel-entertainment/400/400',
  WATER_FILTRATION: 'https://picsum.photos/seed/water-filtration/400/400',
  SECURITY_GUARDS: 'https://picsum.photos/seed/security-guards/400/400',
  OTHER_SERVICES: 'https://picsum.photos/seed/other-services/400/400',
};

const getListingImage = (listing: Listing): string => {
  if (listing.images && listing.images.length > 0 && listing.images[0]) {
    return listing.images[0];
  }
  const category = listing.category?.toUpperCase() || '';
  return categoryPlaceholders[category] || `https://picsum.photos/seed/${listing.id || 'default'}/400/400`;
};

const ListingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAll, loading } = useListing();
  const [listings, setListings] = useState<Listing[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'newest');

  // Read search query from URL (set by Header search form, Hero search, or trending tags)
  const searchQuery = searchParams.get('search') || '';

  // Keep state synchronized with URL search params (important for back/forward navigation and link clicks)
  useEffect(() => {
    setCategory(searchParams.get('category') || '');
    setLocation(searchParams.get('location') || '');
    setSortBy(searchParams.get('sortBy') || 'newest');
  }, [searchParams]);

  const loadListings = useCallback(async () => {
    try {
      let sortParam: string | string[] | undefined = undefined;
      if (sortBy === 'newest') {
        sortParam = ['featured,desc', 'createdAt,desc'];
      } else if (sortBy === 'oldest') {
        sortParam = 'createdAt,asc';
      } else if (sortBy === 'price-low') {
        sortParam = 'price,asc';
      } else if (sortBy === 'price-high') {
        sortParam = 'price,desc';
      } else if (sortBy === 'popular') {
        sortParam = 'views,desc';
      }

      const result = await getAll({
        category: category || undefined,
        location: location || undefined,
        search: searchQuery || undefined,
        sort: sortParam,
        limit: 20,
      });
      // Spring Data Page returns { content: [...] }; fall back to .items or plain array
      const items = result?.content ?? result?.items ?? (Array.isArray(result) ? result : []);
      setListings(items);
    } catch (err) {
      console.error('Failed to load listings:', err);
      setListings([]);
    }
  }, [category, location, sortBy, searchQuery, getAll]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const handleCategoryChange = useCallback((val: string) => {
    setCategory(val);
    const newParams = new URLSearchParams(searchParams);
    if (val) {
      newParams.set('category', val.toLowerCase());
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleSortChange = useCallback((val: string) => {
    setSortBy(val);
    const newParams = new URLSearchParams(searchParams);
    if (val && val !== 'newest') {
      newParams.set('sortBy', val);
    } else {
      newParams.delete('sortBy');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearFilter = useCallback((key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
    if (key === 'category') setCategory('');
    if (key === 'location') setLocation('');
  }, [searchParams, setSearchParams]);

  const categories = [
    { value: 'CARS', label: 'Cars' },
    { value: 'BIKES', label: 'Bikes' },
    { value: 'MOBILES', label: 'Mobiles' },
    { value: 'REAL_ESTATE', label: 'Real Estate' },
    { value: 'RENT_ROOMMATES', label: 'House Rents / Room Mates' },
    { value: 'BUS_TRUCKS_AUTOS_VEHICLES', label: 'Bus / Trucks / Autos / Other Vehicles' },
    { value: 'AUTOMOBILE_ACCESSORIES', label: 'Automobile Accessories' },
    { value: 'PETS_AND_HOBBIES', label: 'Pets & Hobbies' },
    { value: 'COMMUNITY_AND_EVENTS', label: 'Community & Events' },
    { value: 'FURNITURE_INTERIORS', label: 'Furniture / Interiors' },
    { value: 'ELECTRONICS_AND_APPLIANCES', label: 'Electronics & Appliances' },
    { value: 'PERSONAL_AND_COMMUNITY', label: 'Personal & Community' },
    { value: 'JOBS_AND_SERVICES', label: 'Jobs & Services' },
    { value: 'FASHION_AND_LIFESTYLE', label: 'Fashion & Lifestyle' },
    { value: 'SAFETY_PROTECTION', label: 'Safety / Protection' },
    { value: 'BUSINESS_FINANCIAL', label: 'Business / Financial' },
    { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services / Trades' },
    { value: 'INSURANCE_LEGAL', label: 'Insurance / Legal' },
    { value: 'MAINTENANCE_AND_REPAIR', label: 'Maintenance & Repair' },
    { value: 'PERSONAL_SERVICES', label: 'Personal Services' },
    { value: 'SALES_AUCTIONS', label: 'Sales / Auctions / Discounts' },
    { value: 'GIFTS_SPECIAL_OCCASIONS', label: 'Gifts / Special Occasions' },
    { value: 'TRAVEL_ENTERTAINMENT', label: 'Travel / Entertainment' },
    { value: 'WATER_FILTRATION', label: 'Water Filtration / Purification' },
    { value: 'SECURITY_GUARDS', label: 'Security Guards' },
    { value: 'OTHER_SERVICES', label: 'Other Services' },
  ];



  if (loading && listings.length === 0) {
    return <LoadingSpinner />;
  }

  const seoTitle = searchQuery
    ? `"${searchQuery}" — Search Results`
    : category
      ? `${formatCategory(category)} Classifieds`
      : 'Browse Listings';
  const seoDescription = searchQuery
    ? `Search results for "${searchQuery}" on MemAds. Find the best deals on India's premium local marketplace.`
    : category
      ? `Browse ${formatCategory(category)} ads on MemAds. Find great deals from verified sellers across India.`
      : 'Browse thousands of classified ads across India. Cars, mobiles, electronics, furniture, and more from verified sellers.';

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO title={seoTitle} description={seoDescription} canonical="/listings" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <h1 className="text-3xl font-bold text-gray-900">Browse Listings</h1>
          {searchQuery && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-sm font-medium text-primary">
              🔍 "{searchQuery}"
              <button 
                onClick={() => clearFilter('search')}
                className="hover:text-red-500 transition ml-1"
                title="Clear search"
              >
                &times;
              </button>
            </span>
          )}
          {category && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-sm font-medium text-primary">
              🗂️ {formatCategory(category)}
              <button 
                onClick={() => clearFilter('category')}
                className="hover:text-red-500 transition ml-1"
                title="Clear category"
              >
                &times;
              </button>
            </span>
          )}
          {location && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/20 px-3 py-1 text-sm font-medium text-accent">
              📍 {location}
              <button 
                onClick={() => clearFilter('location')}
                className="hover:text-red-500 transition ml-1"
                title="Clear location"
              >
                &times;
              </button>
            </span>
          )}
        </div>

        <div className="flex gap-6">
          {/* Filters - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Category</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="category"
                      value=""
                      checked={category === ''}
                      onChange={() => handleCategoryChange('')}
                      className="rounded"
                    />
                    <span className="ml-2 text-gray-700">All Categories</span>
                  </label>
                  {categories.map((cat) => (
                    <label key={cat.value} className="flex items-center">
                      <input
                        type="radio"
                        name="category"
                        value={cat.value}
                        checked={category.toUpperCase() === cat.value}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="rounded"
                      />
                      <span className="ml-2 text-gray-700">{cat.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Sort By</h3>
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>

              {/* Sponsored Adsterra Units */}
              <div className="pt-4 border-t border-gray-100 flex flex-col items-center">
                <AdsterraBanner320x50 showLabel={false} />
                <AdsterraSmartlinkPromo variant="card" className="w-full mt-4" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filters Button */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow"
              >
                <Filter size={20} />
                Filters
              </button>

              {showFilters && (
                <div className="mt-4 bg-white rounded-lg shadow p-6 space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900">Filters</h3>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Category</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="category"
                          value=""
                          checked={category === ''}
                          onChange={() => handleCategoryChange('')}
                        />
                        <span className="ml-2">All</span>
                      </label>
                      {categories.map((cat) => (
                        <label key={cat.value} className="flex items-center">
                          <input
                            type="radio"
                            name="category"
                            value={cat.value}
                            checked={category.toUpperCase() === cat.value}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                          />
                          <span className="ml-2">{cat.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Sort By</h4>
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="popular">Most Popular</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Listings Grid */}
            {listings.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing) => (
                  <Link
                    key={listing.id}
                    to={`/listings/${listing.id}`}
                    className="group bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden"
                  >
                    <div className="aspect-square bg-gray-200 overflow-hidden">
                      <img
                        src={getListingImage(listing)}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          // Fallback to a unique picsum image based on listing id
                          if (!img.dataset.retried) {
                            img.dataset.retried = 'true';
                            img.src = `https://picsum.photos/seed/${listing.id}/400/400`;
                          }
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-gray-500">{formatCategory(listing.category)}</p>
                      <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-primary">
                        {listing.title}
                      </h3>
                      <p className="text-xl font-bold text-foreground">
                        ₹{(listing.price ?? 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-500 text-lg">No listings found</p>
              </div>
            )}

            {/* Adsterra Native Banner Recommendations */}
            <div className="mt-8">
              <AdsterraNativeBanner />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingsPage;
